import { useQuery } from '@tanstack/react-query';
import { fetchSession, type SessionSnapshot } from './auth.ts';
import { queryKeys } from './keys.ts';

export type { SessionSnapshot };

/**
 * The session, as a query.
 *
 * better-auth ships `useSession()` (a nanostores atom). This uses TanStack Query
 * instead so the session obeys the same invalidation, retry and devtools story
 * as every other server value — and so `beforeLoad` on the authed layout can
 * `ensureQueryData` it without a second fetch on the first render.
 *
 * `retry: false`: a 401 is an answer, not a failure, and retrying it three times
 * just delays the redirect to `/login` by a second.
 */
export function useSessionQuery() {
  return useQuery({
    queryKey: queryKeys.session(),
    queryFn: fetchSession,
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * The active org id, or `null` while the session is still resolving.
 *
 * Every cache key is scoped by it (see `keys.ts`), so a component that reads
 * data must read this from the same place — deriving it twice is how two
 * components end up caching the same rows under two keys.
 */
export function useOrgId(): string | null {
  const { data } = useSessionQuery();
  return data?.activeOrgId ?? null;
}
